<template>
  <Loader :loader="loading.activity" />
  <main v-if="!loading.activity">
    <Button
      class="my-4 btn-simple flex gap-4"
      @click="$router.go(-1)"
      icon="pi pi-chevron-left"
      :label="$t('labels.generic', { en: 'Activities', pt: 'Atividades' })"
    />

    <div>
      <header class="flex justify-between items-center px-8 py-4">
        <h3>{{ activity.title }}</h3>
        <span>
          <Button
            class="btn-simple"
            icon="pi pi-trash"
            @click="confirmDeleteModal(activityId)"
            :label="$t('labels.generic', { en: 'Delete', pt: 'Excluir' })"
          />

          <Button
            class="btn-simple"
            icon="pi pi-pencil"
            @click="goToEdition"
            :label="$t('labels.generic', { en: 'Edit', pt: 'Editar' })"
          />
        </span>
      </header>
      <Loader v-if="loading.activity" :loader="loading.activity" />
      <div
        class="flex flex-col gap-4 px-8 py-4 border border-b-4 border-b-h-primary-500 rounded-md bg-white mx-8"
        v-else
      >
        <span></span>
        <p>
          <strong>{{ "Data de Início: " }}</strong> {{ startDate }}
        </p>
        <p>
          <strong>{{ "Prazo de Entrega: " }}</strong> {{ deliveryDate }}
        </p>
        <p>
          <strong>{{ "Pontuação: " }}</strong>
          {{ activity.scoreRange?.toFixed(1) }}
        </p>
        <p>
          <strong>{{ "Descrição: " }}</strong> <div v-html="activity.description" class="ql-editor"></div>  
        </p>
        <p class="flex flex-col gap-2">
          <strong>{{ "Arquivo de apoio enviado:" }}</strong>
          <span
            v-if="activity.materials && activity.materials.length > 0"
            class="flex flex-wrap gap-2"
          >
            <div
              v-for="(material, index) in activity.materials"
              :key="index"
              class="bg-h-primary-50 py-2 border rounded-md min-w-[200px] md:min-w-[300px] flex justify-between items-center gap-2 cursor-pointer"
              @click.prevent="downloadMaterial(material)"
            >
              <Button
                icon="pi pi-file"
                class="btn-simple"
                :label="material.name"
              />
              <Button icon="pi pi-download" class="btn-simple" />
            </div>
          </span>
        </p>
      </div>
    </div>
    <div class="p-8">
      <Table
        :slotData="studentActivities"
        :columns="columnsData"
        :actions="actions"
        :filters="filters"
        :loading="false"
        :hasStatus="true"
        :hasActions="true"
        :params="params"
        @onFilter="filterFor()"
        @onToggleFilter="(e, type) => toggleFilter(e, type)"
        @onGoTo="(e) => goToCorrection(e)"
        @onClearFilter="clearFilter()"
        :actionsTitle="$t('labels.generic', { pt: 'Ações', en: 'Actions' })"
        @onPaginate="paginate($event)"
      >
      <template #filters>
          <Dropdown   v-if="filterType == 'status'" 
          :options="statusOptions" 
          :optionLabel="'label'" 
          :optionValue="'value'" 
          v-model="filters.status"
          placeholder="Selecione o status"
          />
          <InputText v-if="filterType == 'name'" v-model="filters.name" placeholder="Digite o nome do aluno"/>
       </template>
    </Table>
      <ActivityCorrection
        :studentActivity="selectedStudentActivity"
        v-model:show="correctionVisible"
        @studentActivityUpdated="updateStudentActivity"
        :key="selectedStudentActivity?.id"
      />
    </div>
    <ConfirmDialog
      group="activity-card"
      class="custom-dialog"
      :draggable="false"
      :style="{ width: '80vw', maxWidth: '700px' }"
    />
  </main>
</template>
<script lang="ts">
import { defineAsyncComponent, defineComponent } from "vue";
import { ActivityRest } from "@/services/rest/activity.rest";
import { ActivityModel } from "@/models/activity.model";
import moment from "moment";
import { columns, formatResponse } from "./activity.utils";
import { actions } from "../../Content/classes.utils";
import { ToastFactory } from "@/utils/toast-factory.util";
import { Messages } from "@/utils/messages.util";
import { GenericsUtils } from "@/utils/generics.util";
import { MediasRest } from "@/services/rest/media.rest";
import { QueryParams } from "@/models/query-params.model";
const ActivityCorrection = defineAsyncComponent(
  () => import("./ActivityCorrection/activity-correction.vue")
);
export default defineComponent({
  data() {
    return {
      loading: { activity: false, studentsActivities: false },
      activity: {} as ActivityModel,
      activityId: this.$route.params?.id as string,
      studentActivities: [] as any,
      columnsData: columns,
      params: {
        size: 10,
        page: 0,
        total: 0,
      } as QueryParams,
      filters:{
        isCleareable: false,
        status:undefined,
        name:undefined
      },
      filterType:"status",
      actions: [
        {
          title: "Ver Detalhes",
          icon: "hi-chevron-right",
          type: "goTo",
          style: "btn-outline",
        },
      ],
      selectedStudentActivity: {} as any,
      correctionVisible: false,
      statusOptions:[
        {label:"Todos", value:undefined},
        {label:"Corrigido", value:"FEEDBACK"},
        {label:"Aguardando Correção", value:"ASSIGNED"},
      ]
    };
  },
  components: { ActivityCorrection },
  computed: {
    restActivities(): ActivityRest {
      return new ActivityRest();
    },
    mediaRest(): MediasRest {
      return new MediasRest();
    },
    generics(): GenericsUtils {
      return new GenericsUtils();
    },
    startDate(): string {
      if (!this.activity?.startDate) return "";
      const date = this.activity?.startDate!;
      const startDate = new Date(
        date[0],
        date[1] - 1,
        date[2],
        date[3],
        date[4]
      );
      return moment(startDate).locale("pt-br").format("L");
    },
    deliveryDate(): string {
      if (!this.activity?.finalDate) return "";
      const date = this.activity.finalDate!;
      const finalDate = new Date(
        date[0],
        date[1] - 1,
        date[2],
        date[3],
        date[4]
      );
      return moment(finalDate).locale("pt-br").format("LLL");
    },
  },
  methods: {
    getActivity(): void {
      this.loading.activity = true;
      this.restActivities.getById(this.activityId).subscribe({
        next: (response) => {
          this.loading.activity = false;

          this.activity = {
            ...response,
            materials: response.materials.map((material) => ({
              ...material,
              url: this.generics.getSecureUrl(material.url),
              mimetype: this.generics.getMimeType(material.mimetype),
            })),
          };
        },
        error: () => {
          this.loading.activity = false;
        },
        complete: () => {
          this.loading.activity = false;
        },
      });
    },
    getStudentsActivities(): void {
      this.restActivities.getStudentsByActivityId(this.activityId, this.params).subscribe({
        next: (response) => {
          this.loading.studentsActivities = false;
          this.studentActivities = formatResponse(response.content);
          this.params.size = response.size;
          this.params.page = response.page;
          this.params.total = response.total;
        },
        error: () => {
          this.loading.studentsActivities = false;
          this.studentActivities = [];
        },
        complete: () => {
          this.loading.studentsActivities = false;
        },
      });
    },
    deleteActivity(id: string): void {
      this.restActivities.delete(id).subscribe({
        next: () => {
          this.$toast.add(ToastFactory.success(Messages.ACTIVITY_DELETED));
          this.$router.go(-1);
        },
        error: () => {},
      });
    },
    confirmDeleteModal(id: string): void {
      this.$confirm.require({
        group: "activity-card",
        message: this.$t("labels.generic", {
          pt: "Tem certeza que quer excluir essa atividade?  Ao excluir, essa atividade não poderá ser recuperada",
          en: "Are you sure on delete this activity? After deleted, this activity won't be recovered ",
        }),
        header: this.$t("labels.generic", {
          pt: "Excluir atividade",
          en: "Delete activity",
        }),
        acceptLabel: this.$t("labels.generic", { pt: "Excluir", en: "Delete" }),
        rejectLabel: this.$t("labels.generic", {
          pt: "Cancelar",
          en: "Cancel",
        }),
        rejectClass: "btn-simple",
        acceptClass: "btn-delete-block",
        accept: () => {
          this.deleteActivity(id);
        },
      });
    },
    goToEdition(): void {
      this.$router.push({
        name: "formActivity",
        params: {
          id: this.activityId,
        },
      });
    },
    goToCorrection(event: any): void {
      this.selectedStudentActivity = event;
      this.correctionVisible = true;
    },
    async downloadMaterial(material: any) {
      this.mediaRest.getMedia(material.id).subscribe({
        next: async (response) => {
          const mediaUrl = this.generics.getSecureUrl(response.url);
          try {
            const fetchResponse = await fetch(mediaUrl);
            const blob = await fetchResponse.blob();
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = blobUrl;
            link.download = response.name || "download";
            document.body.appendChild(link);
            link.click();

            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);
          } catch (error) {
            console.error("Erro ao fazer download:", error);
          }
        },
      });
    },
    updateStudentActivity() {
      this.getStudentsActivities();
    },
    filterFor(){
      this.filters.isCleareable = true;
      const params = {
        status:this.filters.status,
        name:this.filters.name
      } as QueryParams
      this.setPage(params);
      this.getStudentsActivities()
    },
    setPage(params:QueryParams){
      this.params ={
        ...params, 
        page:0,
        size:10
      }
    },
    toggleFilter(event: any, type: string): void {
      this.filterType = type;
    },
    clearFilter():void{
      this.filters.isCleareable = false;
      this.filters.status = undefined;
      this.filters.name = undefined;
      this.params = {
        size: 10,
        page: 0,
        total: 0,
      } as QueryParams;
      this.getStudentsActivities();
      
    },
    paginate(event: { page: number; rows: number }): void {
      this.params.page = event.page;
      this.params.size = event.rows;
      this.getStudentsActivities();
    },
  },
  mounted() {
    this.getActivity();
    this.getStudentsActivities();
  },
});
</script>
<style lang="scss">
.custom-dialog {
  .p-dialog-content {
    min-height: 100px;
  }
}
</style>
